import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, ExternalLink, Settings, FileText } from "lucide-react";
import { useJotFormForms } from "@/hooks/useJotFormForms";
import { useProject } from "@/hooks/useProject";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/ui/data-table";

export default function JotFormFormsList() {
  const navigate = useNavigate();
  const { projectId } = useProject();
  const {
    configuredForms,
    isLoadingConfigured,
    jotformForms,
    isLoadingJotform,
    refetch,
  } = useJotFormForms();

  const [showNewFormDialog, setShowNewFormDialog] = useState(false);

  // Filter JotForm API forms to only show unconfigured ones
  const unconfiguredForms = jotformForms.filter(
    (apiForm) =>
      !configuredForms.some((config) => config.jotform_form_id === apiForm.id)
  );

  // Filter configured forms by current project
  const projectForms = configuredForms;

  const handleConfigureForm = (formId: string) => {
    // Find the configured form
    const configuredForm = configuredForms.find(
      (f) => f.jotform_form_id === formId
    );

    if (configuredForm) {
      navigate(`/admin/forms/${configuredForm.id}`);
    } else {
      // For new forms, navigate to create mode
      navigate(`/admin/forms/new?jotform_id=${formId}`);
    }
  };

  return (
    <div className="space-y-6">
      <DataTable
        title="Formularios JotForm"
        description="Gestiona la configuración de tus formularios de JotForm"
        data={projectForms}
        isLoading={isLoadingConfigured}
        onCreateNew={() => setShowNewFormDialog(true)}
        createButtonLabel="Nuevo Formulario"
        emptyIcon={<FileText className="w-10 h-10 text-muted-foreground" />}
        emptyTitle="No hay formularios configurados"
        emptyDescription="No hay formularios configurados para este proyecto. Usa el botón 'Nuevo Formulario' para agregar uno."
        actions={
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoadingJotform}
          >
            {isLoadingJotform ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Actualizar
          </Button>
        }
        columns={[
          {
            header: "Título",
            render: (form) => (
              <span className="font-medium">
                {form.form_title || form.jotform_form_id}
              </span>
            ),
          },
          {
            header: "Tipo",
            render: (form) =>
              form.is_referral_form ? (
                <Badge
                  variant="secondary"
                  className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200"
                >
                  Referido
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200"
                >
                  Principal
                </Badge>
              ),
          },
          {
            header: "Umbral",
            render: (form) => `${form.validation_threshold}%`,
          },
          {
            header: "Formulario Padre",
            render: (form) =>
              form.parent?.form_title || (
                <span className="text-muted-foreground">-</span>
              ),
          },
          {
            header: "Acciones",
            className: "text-right",
            render: (form) => (
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(
                      `https://www.jotform.com/form/${form.jotform_form_id}`,
                      "_blank"
                    );
                  }}
                  title="Abrir en JotForm"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfigureForm(form.jotform_form_id);
                  }}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Configurar
                </Button>
              </div>
            ),
          },
        ]}
        onRowClick={(form) => handleConfigureForm(form.jotform_form_id)}
      />

      {/* New Form Dialog */}
      <Dialog open={showNewFormDialog} onOpenChange={setShowNewFormDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Seleccionar Formulario de JotForm</DialogTitle>
            <DialogDescription>
              Elige un formulario de JotForm para configurar en este proyecto
            </DialogDescription>
          </DialogHeader>

          {isLoadingJotform ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : unconfiguredForms.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Todos los formularios disponibles ya están configurados.
            </div>
          ) : (
            <div className="space-y-2">
              {unconfiguredForms.map((form) => (
                <Card
                  key={form.id}
                  className="cursor-pointer hover:bg-accent transition-colors"
                  onClick={() => {
                    setShowNewFormDialog(false);
                    handleConfigureForm(form.id);
                  }}
                >
                  <CardHeader className="py-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-base">
                          {form.title}
                        </CardTitle>
                        <CardDescription className="text-xs">
                          ID: {form.id} • {form.count || 0} respuestas
                        </CardDescription>
                      </div>
                      <Badge
                        variant={
                          form.status === "ENABLED" ? "default" : "secondary"
                        }
                      >
                        {form.status}
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
